import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  notification,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import SharedService from "../../../services/shared/service";
import { useAuth } from "oidc-react";
import SEO from "../../../components/SEO";
import SupplierAutoComplete from "../../../components/AutoComplete/Supplier/SupplierAutoComplete";

const OCR_CONFIGURATION_PATH = "ocrconfiguration";
// POST (create), POST /search, PATCH (update) and DELETE /{id}.
const OCR_CONFIGURATION_SEARCH_PATH = `${OCR_CONFIGURATION_PATH}/search`;

const pageSEOSettings = {
  title: "OCR Configuration",
};

// OcrConfigurationDataDTO's server-side defaults.
const DEFAULT_MODEL = "prebuilt-invoice";
const DEFAULT_LOCALE = "en-uk";

const listValue = (values) =>
  Array.isArray(values) ? values.filter(Boolean).join(", ") : "";

// ConfigurationType, from /reference/configuration/types. The shape is not
// fixed across the reference endpoints, so accept a plain string or { id, value }.
const toTypeOption = (item) => {
  if (typeof item === "string") return { label: item, value: item, key: item };
  const value = item?.id ?? item?.value;
  return {
    label: item?.value ?? item?.name ?? String(value),
    value,
    key: value,
  };
};

const OCRConfiguration = () => {
  const { userData } = useAuth();
  const token = userData?.access_token;

  const [configurations, setConfigurations] = useState([]);
  const [configurationTypes, setConfigurationTypes] = useState([]);
  const [filters, setFilters] = useState({ supplierName: "" });
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [sortedInfo, setSortedInfo] = useState({});
  const [listPagination, setlistPagination] = useState({
    totalItemCount: 0,
    pageSize: 10,
    pageNumber: 1,
  });

  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const notifyError = (error, fallback) => {
    notification.error({
      message: "Error",
      description: error?.response?.statusText ?? fallback,
    });
  };

  useEffect(() => {
    fetchConfigurationTypes();
  }, []);

  useEffect(() => {
    fetchConfigurations();
  }, [listPagination.pageNumber, sortedInfo, searchTrigger]);

  // With a single type there is nothing to choose, so pre-select it. Runs on
  // open and again if the reference list arrives while the modal is up.
  useEffect(() => {
    if (addModalVisible && configurationTypes.length === 1) {
      addForm.setFieldsValue({ type: configurationTypes[0].value });
    }
  }, [addModalVisible, configurationTypes, addForm]);

  const fetchConfigurationTypes = async () => {
    try {
      const response = await SharedService.get({
        token,
        url: "reference/configuration/types",
      });

      const types = Array.isArray(response) ? response : [];
      setConfigurationTypes(types.map(toTypeOption));
    } catch (error) {
      notifyError(error, "Could not load configuration types.");
    }
  };

  // Matches on the value or, if the enum serialises as a string, the member name.
  const configurationTypeLabel = (type) =>
    configurationTypes.find(
      (option) => option.value === type || option.label === type,
    )?.label ??
    type ??
    "";

  const fetchConfigurations = async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: listPagination.pageNumber,
        pageSize: listPagination.pageSize,
        orderBy:
          sortedInfo.columnKey && sortedInfo.order
            ? `${sortedInfo.columnKey}:${
                sortedInfo.order === "ascend" ? "Asc" : "Desc"
              }`
            : "id:Asc",
      };

      // The search request fields are unconfirmed, so only the supplier name is
      // sent and only when it is set.
      const data = {
        ...(filters.supplierName !== "" && {
          supplierName: filters.supplierName,
        }),
      };

      const response = await SharedService.post({
        token,
        url: OCR_CONFIGURATION_SEARCH_PATH,
        data,
        params,
      });

      setConfigurations(response?.items ?? []);
      setlistPagination((prev) => ({
        ...prev,
        totalItemCount: response?.totalItemCount ?? 0,
      }));
    } catch (error) {
      notifyError(error, "Could not load supplier configurations.");
    } finally {
      setLoading(false);
    }
  };

  // OcrConfigurationDTO. UpdatedBy/UpdatedOn/CreatedBy/CreatedOn are internal on
  // the server, so they are neither sent nor returned.
  const buildPayload = (values) => ({
    supplierId: values.supplierId,
    supplierName: values.supplierName,
    customerId: values.customerId,
    type: values.type,
    configurationData: {
      queryFields: values.queryFields ?? [],
      pages: values.pages ?? [],
      model: values.model,
      locale: values.locale,
    },
  });

  const addConfiguration = async (values) => {
    setLoading(true);
    try {
      await SharedService.post({
        token,
        url: OCR_CONFIGURATION_PATH,
        data: buildPayload(values),
      });
      notification.success({ message: "Added Successfully" });
      fetchConfigurations();
    } catch (error) {
      notifyError(error, "Could not add the configuration.");
    } finally {
      setLoading(false);
    }
  };

  const editConfiguration = async (values) => {
    setLoading(true);
    try {
      await SharedService.patch({
        token,
        url: OCR_CONFIGURATION_PATH,
        data: { id: values.id, ...buildPayload(values) },
      });
      notification.success({ message: "Updated Successfully" });
      fetchConfigurations();
    } catch (error) {
      notifyError(error, "Could not update the configuration.");
    } finally {
      setLoading(false);
    }
  };

  const deleteConfiguration = async (id) => {
    setLoading(true);
    try {
      await SharedService.delete({
        token,
        url: `${OCR_CONFIGURATION_PATH}/${id}`,
      });
      notification.success({ message: "Deleted Successfully" });
      fetchConfigurations();
    } catch (error) {
      notifyError(error, "Could not delete the configuration.");
    } finally {
      setLoading(false);
    }
  };

  const runSearch = () => {
    setlistPagination((prev) => ({ ...prev, pageNumber: 1 }));
    setSearchTrigger((n) => n + 1);
  };

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value ?? "" }));

  const showAddModal = () => {
    addForm.resetFields();
    setAddModalVisible(true);
  };

  const showEditModal = (record) => {
    // SupplierAutoComplete seeds itself from { id, name }.
    setEditingSupplier(
      record.supplierId
        ? { id: record.supplierId, name: record.supplierName }
        : null,
    );
    editForm.setFieldsValue({
      id: record.id,
      supplierId: record.supplierId,
      supplierName: record.supplierName,
      customerId: record.customerId,
      type: record.type,
      queryFields: record.configurationData?.queryFields ?? [],
      pages: record.configurationData?.pages ?? [],
      model: record.configurationData?.model,
      locale: record.configurationData?.locale,
    });
    setEditModalVisible(true);
  };

  const showDeleteModal = (record) => {
    setSelectedRecord(record);
    setDeleteModalVisible(true);
  };

  const handleCancel = () => {
    setAddModalVisible(false);
    setEditModalVisible(false);
    setDeleteModalVisible(false);
  };

  const handleAdd = () => {
    addForm.validateFields().then((values) => {
      addConfiguration(values);
      addForm.resetFields();
      setAddModalVisible(false);
    });
  };

  const handleEdit = () => {
    editForm.validateFields().then((values) => {
      editConfiguration(values);
      setEditModalVisible(false);
    });
  };

  const handleDelete = () => {
    if (selectedRecord) {
      deleteConfiguration(selectedRecord.id);
    }
    setSelectedRecord(null);
    setDeleteModalVisible(false);
  };

  const handleTableChange = (pagination, tableFilters, sorter) => {
    setSortedInfo(sorter);
    setlistPagination((prev) => ({ ...prev, pageNumber: pagination.current }));
  };

  const paginationProps = {
    showSizeChanger: false,
    total: listPagination.totalItemCount,
    pageSize: listPagination.pageSize,
    current: listPagination.pageNumber,
  };

  const dataColumn = (title, key) => ({
    title,
    key,
    dataIndex: "configurationData",
    render: (data) => (
      <Tooltip title={data?.[key]} placement="topLeft">
        <div>{data?.[key]}</div>
      </Tooltip>
    ),
  });

  const tagColumn = (title, key, color) => ({
    title,
    key,
    dataIndex: "configurationData",
    render: (data) => {
      const values = Array.isArray(data?.[key])
        ? data[key].filter(Boolean)
        : [];
      if (values.length === 0) return "";
      return (
        <Tooltip title={listValue(values)} placement="topLeft">
          <div className="flex flex-wrap gap-1">
            {values.map((value) => (
              <Tag key={value} color={color} className="!m-0">
                {value}
              </Tag>
            ))}
          </div>
        </Tooltip>
      );
    },
  });

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
      width: 90,
      sorter: true,
      sortOrder: sortedInfo.columnKey === "id" && sortedInfo.order,
      render: (id) => (
        <Tooltip title={id} placement="topLeft">
          <div>{id}</div>
        </Tooltip>
      ),
    },
    {
      title: "Supplier",
      dataIndex: "supplierName",
      key: "supplierName",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "supplierName" && sortedInfo.order,
      render: (supplierName, record) => (
        <Tooltip title={record.supplierId} placement="topLeft">
          <div>
            <div>{supplierName}</div>
            <div className="cell-sub">{record.supplierId}</div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      sorter: true,
      sortOrder: sortedInfo.columnKey === "type" && sortedInfo.order,
      render: (type) => (
        <Tooltip title={configurationTypeLabel(type)} placement="topLeft">
          <div>{configurationTypeLabel(type)}</div>
        </Tooltip>
      ),
    },
    dataColumn("Model", "model"),
    dataColumn("Locale", "locale"),
    tagColumn("Pages", "pages", "blue"),
    tagColumn("Query Fields", "queryFields", "purple"),
    {
      title: <div className="flex justify-end">Actions</div>,
      key: "actions",
      width: 120,
      render: (text, record) => (
        <div className="flex justify-end gap-3">
          <Tooltip title="Edit">
            <EditOutlined
              style={{ fontSize: 20 }}
              onClick={() => showEditModal(record)}
              className="cursor-pointer"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined
              style={{ fontSize: 20 }}
              onClick={() => showDeleteModal(record)}
              className="cursor-pointer text-red-500"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const fieldHelp = (text) => (
    <span className="text-[12px] text-[#7A849A]">{text}</span>
  );

  const fieldLabel = (text) => (
    <span className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide">
      {text}
    </span>
  );

  // SupplierAutoComplete resolves the supplier for us, so the form only keeps
  // the id and name it hands back.
  const configurationFields = (form, supplier) => (
    <>
      <Form.Item
        name="supplierId"
        label={fieldLabel("Supplier")}
        rules={[{ required: true, message: "Please select a supplier." }]}
      >
        <SupplierAutoComplete
          user={{ token }}
          supplier={supplier}
          onSelectSupplier={(option) => {
            form.setFieldsValue({
              supplierId: option?.value,
              supplierName: option?.label,
            });
          }}
        />
      </Form.Item>
      <Form.Item name="supplierName" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="customerId" hidden>
        <Input />
      </Form.Item>
      <Form.Item
        name="type"
        label={fieldLabel("Type")}
        rules={[{ required: true, message: "Please select the type." }]}
      >
        <Select placeholder="Select the type" options={configurationTypes} />
      </Form.Item>
      <Form.Item name="model" label={fieldLabel("Model")}>
        <Input placeholder={DEFAULT_MODEL} />
      </Form.Item>
      <Form.Item name="locale" label={fieldLabel("Locale")}>
        <Input placeholder={DEFAULT_LOCALE} />
      </Form.Item>
      <Form.Item
        name="pages"
        label={fieldLabel("Pages")}
        extra={fieldHelp(
          "Which pages of the document to read — a single page (1), a range (1-3), or several entries. Leave empty to read every page.",
        )}
      >
        <Select
          mode="tags"
          open={false}
          placeholder="Type a page and press Enter"
          tokenSeparators={[","]}
        />
      </Form.Item>
      <Form.Item
        name="queryFields"
        label={fieldLabel("Query Fields")}
        extra={fieldHelp(
          "Extra fields to pull out of the document on top of what the model returns by default. Enter one field name per entry.",
        )}
      >
        <Select
          mode="tags"
          open={false}
          placeholder="Type a field and press Enter"
          tokenSeparators={[","]}
        />
      </Form.Item>
    </>
  );

  return (
    <div>
      <SEO {...pageSEOSettings} />
      <div className="px-4 sm:px-7 py-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[22px] font-semibold text-[#0B1220] leading-tight">
              OCR Configuration
            </div>
            <div className="text-[13px] text-[#7A849A] mt-1">
              OCR configurations applied to documents from each supplier.
            </div>
          </div>
          <Button type="primary" onClick={showAddModal}>
            Add configuration
          </Button>
        </div>
      </div>

      <div className="px-4 sm:px-7 pb-7 relative">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <Input
            placeholder="Search supplier name…"
            prefix={<SearchOutlined className="text-[#7A849A]" />}
            className="w-[280px]"
            value={filters.supplierName}
            allowClear
            onChange={(e) => updateFilter("supplierName", e.target.value)}
            onPressEnter={runSearch}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={runSearch}>
            Search
          </Button>
        </div>

        {loading && (
          <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-10 rounded-[14px]">
            <Spin size="large" className="custom-spin" />
          </div>
        )}
        <Table
          dataSource={configurations}
          rowKey="id"
          columns={columns}
          pagination={paginationProps}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </div>

      {/* Add Modal */}
      <Modal
        title="Add configuration"
        open={addModalVisible}
        onOk={handleAdd}
        onCancel={handleCancel}
        okText="Add"
        cancelText="Cancel"
      >
        <Form
          form={addForm}
          layout="vertical"
          initialValues={{ model: DEFAULT_MODEL, locale: DEFAULT_LOCALE }}
        >
          {configurationFields(addForm, null)}
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit configuration"
        open={editModalVisible}
        onOk={handleEdit}
        onCancel={handleCancel}
        okText="Save"
        cancelText="Cancel"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="id" label={fieldLabel("ID")}>
            <Input readOnly disabled={true} />
          </Form.Item>
          {configurationFields(editForm, editingSupplier)}
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        title="Delete configuration"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={handleCancel}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        centered
      >
        <div className="text-[14px] text-[#374152]">
          Are you sure you want to delete the configuration for{" "}
          {selectedRecord?.supplierName ?? selectedRecord?.supplierId}?
        </div>
      </Modal>
    </div>
  );
};

export default OCRConfiguration;
